import React, { useEffect } from 'react';
import { TopBanner, Nav, Footer, PLAY_URL } from './SiteChrome';
import './site.css';

const FEATURES = [
  { ic: '💞', t: 'Smart Matching', d: 'Swipe through people who fit your vibe, interests and filters — age, height, distance and more.' },
  { ic: '🛡️', t: 'Verified & Safe', d: 'WhatsApp/email OTP login and profile verification keep the community real and secure.' },
  { ic: '🎥', t: 'Audio & Video Calls', d: 'Go beyond chat — connect face-to-face with in-app audio and video calls once you match.' },
  { ic: '⭐', t: 'Super Likes & Boost', d: 'Stand out with Super Likes and get seen by more people with Boost.' },
  { ic: '💬', t: 'Real-time Chat', d: 'Instant messaging with typing indicators, online presence and read receipts.' },
  { ic: '👑', t: 'Premium Perks', d: 'See who likes you, unlimited swipes, and priority visibility with membership.' },
];

const STEPS = [
  { n: '1', t: 'Create your profile', d: 'Sign up in seconds with OTP, add photos and your interests.' },
  { n: '2', t: 'Discover & swipe', d: 'Browse curated profiles that match your preferences.' },
  { n: '3', t: 'Match & chat', d: 'Like each other to match, then chat, call and meet up.' },
  { n: '4', t: 'Find your spark', d: 'Build real connections that go beyond the screen.' },
];

const PLANS = [
  { badge: '💎', name: 'Premium', price: '₹199', per: '/week', pop: false, perks: ['Unlimited likes', 'See who likes you', '5 Super Likes / day'] },
  { badge: '👑', name: 'Gold', price: '₹599', per: '/month', pop: true, perks: ['Everything in Premium', 'Audio + Video calls', 'Advanced filters', 'Priority profile'] },
  { badge: '🌟', name: 'Platinum', price: '₹1499', per: '/3 months', pop: false, perks: ['Everything in Gold', 'Unlimited everything', 'Weekly Boosts', 'Top picks'] },
];

const QUOTES = [
  { q: 'Matched with someone amazing in my first week. The video call feature made it feel real before we even met!', who: '— Aanya, Mumbai' },
  { q: 'Finally a dating app that feels safe. Verification and the clean design make all the difference.', who: '— Rohan, Delhi' },
  { q: 'The filters actually work. I met people who genuinely share my interests.', who: '— Ishita, Pune' },
];

const FAQS = [
  { q: 'Is SparkSync free to use?', a: 'Yes! You can sign up, create a profile, swipe and match for free. Premium memberships unlock extra features like unlimited likes, seeing who liked you, and calls.' },
  { q: 'How do you keep the app safe?', a: 'We use OTP-based login, profile verification, and give you controls to block, unmatch and report anyone. Our team reviews reports and support tickets.' },
  { q: 'Can I use it on iPhone and Android?', a: 'SparkSync is available on Android, with iOS coming soon.' },
  { q: 'How do I delete my account?', a: 'Visit our Delete Account page and submit a request, or contact support. We remove your account and data as described in our Privacy Policy.' },
];

export default function Landing() {
  useEffect(() => { document.title = 'SparkSync – Connect. Match. Spark.'; }, []);
  return (
    <div className="site">
      <TopBanner />
      <Nav />

      {/* 1. Hero */}
      <header className="hero wrap">
        <img className="logo" src="/logo.png" alt="SparkSync logo" />
        <h1>Connect. Match. <span className="grad-text">Spark.</span></h1>
        <p>Meet real people who match your vibe. Chat, call, and find your spark — all in one beautiful app.</p>
        <div className="cta">
          <a className="btn-grad" href={PLAY_URL} target="_blank" rel="noreferrer">▶ Get it on Google Play</a>
          <a className="btn-ghost" href="#features">See features</a>
        </div>
        <div className="stars">⭐️⭐️⭐️⭐️⭐️ Loved by thousands of singles</div>
      </header>

      {/* 2. Features */}
      <section className="section" id="features">
        <div className="wrap">
          <div className="eyebrow">Why SparkSync</div>
          <div className="h2">Everything you need to find a real connection</div>
          <p className="lead">Thoughtful features that help you meet the right people — safely, and with a spark.</p>
          <div className="grid">
            {FEATURES.map((f) => (
              <div className="feature" key={f.t}>
                <div className="ic">{f.ic}</div>
                <h3>{f.t}</h3>
                <p>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <section className="section alt" id="how">
        <div className="wrap">
          <div className="eyebrow">How it works</div>
          <div className="h2">Find your spark in 4 simple steps</div>
          <div className="steps">
            {STEPS.map((s) => (
              <div className="step" key={s.n}>
                <div className="num">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Plans */}
      <section className="section" id="plans">
        <div className="wrap">
          <div className="eyebrow">Membership</div>
          <div className="h2">Upgrade for more connections</div>
          <p className="lead">Start free. Go premium anytime for unlimited likes, calls and more.</p>
          <div className="plans">
            {PLANS.map((p) => (
              <div className={`plan ${p.pop ? 'pop' : ''}`} key={p.name}>
                <div className="badge">{p.badge}</div>
                <h3>{p.name}</h3>
                <div className="price">{p.price}<span style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 600 }}>{p.per}</span></div>
                <ul>{p.perks.map((x) => <li key={x}>✓ {x}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Testimonials */}
      <section className="section alt">
        <div className="wrap">
          <div className="eyebrow">Real stories</div>
          <div className="h2">People are finding their spark</div>
          <div className="quotes">
            {QUOTES.map((q) => (
              <div className="quote" key={q.who}>
                <p>“{q.q}”</p>
                <div className="who">{q.who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="section" id="faq">
        <div className="wrap">
          <div className="eyebrow">FAQ</div>
          <div className="h2">Questions, answered</div>
          <div className="faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Download CTA */}
      <section className="wrap">
        <div className="cta-band">
          <h2>Ready to find your <span className="grad-text">spark</span>?</h2>
          <p>Download SparkSync and start matching today.</p>
          <a className="btn-grad" href={PLAY_URL} target="_blank" rel="noreferrer">▶ Get it on Google Play</a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
